// screenControls — the buttons on the caller's CURRENT screen that the support
// agent may point at, by their visible label (Talk to support, 22 Oct 2026).
//
// The hand-tagged catalogue in supportGuide.ts covers a few buttons on a few
// pages. This lets the agent ring any ordinary control on the page the caller
// is looking at ("button:Save", "button:New delivery") without a tag on each.
//
// What leaves the browser is only the LABEL list, in a tool result the vendor
// keeps for 30 days. So a label is shared only when it reads like UI wording:
//   - never a control inside a table, grid or list row (rows carry grower,
//     worker and pallet names);
//   - never a label with a digit (pallet numbers, amounts, dates);
//   - never the support panel's own controls;
//   - 2 to 40 characters, at most MAX_CONTROLS labels.
// The agent can only point: the ring is drawn over the element and never clicks.

export const MAX_CONTROLS = 40

// Form fields too, by their LABEL (never their value): walk-throughs reach "type
// the bin count here". Passwords and hidden inputs never.
const FIELD_SELECTOR = 'input:not([type="hidden"]):not([type="password"]), select, textarea'
const CONTROL_SELECTOR = [
  'button', 'a[href]', '[role="button"]', '[role="tab"]', '[role="menuitem"]', '[role="link"]', 'summary', FIELD_SELECTOR,
].join(', ')

/** A box to type in or pick from (as opposed to a button): tapping it is not the step done. */
export const isField = (el: Element): boolean => el.matches(FIELD_SELECTOR)

/** A field is named by its aria-label, else its <label>, else its placeholder; never its value. */
function fieldLabel(el: Element): string {
  const named = el.getAttribute('aria-label')
    || (el as HTMLInputElement).labels?.[0]?.textContent
    || el.getAttribute('placeholder')
    || ''
  return named.replace(/\s*\*\s*$/, '')
}

// Rows and grids hold people's and records' names; the panel is our own UI.
const EXCLUDED_ANCESTORS = 'table, [role="grid"], [role="row"], [role="rowgroup"], [data-support-panel], [data-no-support-scan]'

/** The label a person sees or hears for a control: aria-label, else its text. */
export function controlLabel(el: Element): string {
  const aria = el.getAttribute('aria-label')
  const raw = isField(el) ? fieldLabel(el) : aria && aria.trim() ? aria : (el.textContent ?? '')
  return raw.replace(/\s+/g, ' ').trim()
}

/** Is this label safe to send, and useful to say out loud? Pure. */
export function isShareableLabel(label: string): boolean {
  if (label.length < 2 || label.length > 40) return false
  if (/\d/.test(label)) return false
  return /[a-z]/i.test(label)
}

export interface ScreenControl { label: string; el: Element }

/**
 * The visible, shareable controls under `root`, first seen first, one per label.
 * `isVisible` is injected so the rule is testable without a layout engine.
 */
export function scanControls(root: ParentNode, isVisible: (el: Element) => boolean): ScreenControl[] {
  const out: ScreenControl[] = []
  const seen = new Set<string>()
  for (const el of Array.from(root.querySelectorAll(CONTROL_SELECTOR))) {
    if (out.length >= MAX_CONTROLS) break
    if (el.closest(EXCLUDED_ANCESTORS) || el.getAttribute('aria-hidden') === 'true') continue
    const label = controlLabel(el)
    const key = label.toLowerCase()
    if (!isShareableLabel(label) || seen.has(key) || !isVisible(el)) continue
    seen.add(key)
    out.push({ label, el })
  }
  return out
}

/**
 * The control the agent asked for: an exact label (any case) first, then the one
 * control whose label starts with it, then the one that contains it. Two or more
 * loose matches is no match: ringing the wrong button is worse than saying so.
 */
export function findControl(controls: ScreenControl[], wanted: string): ScreenControl | null {
  const w = wanted.replace(/\s+/g, ' ').trim().toLowerCase()
  if (!w) return null
  const exact = controls.find(c => c.label.toLowerCase() === w)
  if (exact) return exact
  for (const test of [(l: string) => l.startsWith(w), (l: string) => l.includes(w)]) {
    const hits = controls.filter(c => test(c.label.toLowerCase()))
    if (hits.length === 1) return hits[0]
    if (hits.length > 1) return null
  }
  return null
}

/** "button:<label>" → the label, or null for any other target. */
export function parseControlTarget(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const m = /^button:(.+)$/i.exec(raw.trim())
  return m ? m[1].trim() || null : null
}

/**
 * What the panel tells the agent when the caller taps the button she rang ("she
 * sees you tap", support plan phase 2). Sent as a user message so it starts her
 * next turn; the prompt reads "(tapped: …)" as "they did that step, go on".
 * The label is the one she rang, already a shareable UI label.
 */
export function tappedMessage(label: string): string {
  return `(tapped: ${label.replace(/\s+/g, ' ').trim().slice(0, 40)})`
}

// ---------------------------------------------------------------------------
// Errors on screen ("she sees errors", support plan phase 2). An error the
// caller can see goes into "Screen now:" so "it won't save" gets a real answer.
// Same rule as the buttons: never from a table, grid or row, never the panel.
// Error sentences can carry data, so before anything leaves the browser every
// digit becomes # and an email becomes [email]; at most MAX_ERRORS, each short.
//
// POPIA (review, 27 Oct): an alert's TEXT is shared only when the element opts
// in with data-support-share, i.e. it shows the app's own fixed wording. Server
// messages can name a person ("That PUC does not belong to <grower>"), so any
// other alert becomes "An error is showing near <field label>".
// ---------------------------------------------------------------------------
export const MAX_ERRORS = 5
const ERROR_SELECTOR = '[role="alert"], [aria-invalid="true"]'

/** An error message made safe to send: emails and digits hidden, squeezed, capped. */
export function shareableError(text: string): string | null {
  const s = text
    .replace(/\S+@\S+\.\S+/g, '[email]')
    .replace(/\d+([.,:/-]\d+)*/g, '#')
    .replace(/\s+/g, ' ')
    .trim()
  if (s.length < 4 || !/[a-z]{2}/i.test(s)) return null
  return s.length > 160 ? `${s.slice(0, 157)}…` : s
}

const SHARE_ATTR = 'data-support-share'

/** A field's label, only when it reads like UI wording (no digits, short). */
function labelOf(el: Element): string {
  const named = fieldLabel(el).replace(/\s+/g, ' ').trim()
  return isShareableLabel(named) ? named : ''
}

/** A field marked invalid is named by its label: "<label> needs fixing". */
function invalidFieldText(el: Element): string {
  const named = labelOf(el)
  return named ? `${named} needs fixing` : ''
}

/** The labelled field an alert sits next to: up to three levels out. */
function nearbyFieldLabel(el: Element): string {
  let box: Element | null = el.parentElement
  for (let i = 0; box && i < 3; i++, box = box.parentElement) {
    for (const f of Array.from(box.querySelectorAll(FIELD_SELECTOR))) {
      const named = labelOf(f)
      if (named) return named
    }
  }
  return ''
}

/** What she may hear about an alert: its text only if it opts in, else where it is. */
function alertText(el: Element): string {
  if (el.closest(`[${SHARE_ATTR}]`)) return el.textContent ?? ''
  const near = nearbyFieldLabel(el)
  return near ? `An error is showing near ${near}` : 'An error is showing on this screen'
}

export function scanErrors(root: ParentNode, isVisible: (el: Element) => boolean): string[] {
  const out: string[] = []
  for (const el of Array.from(root.querySelectorAll(ERROR_SELECTOR))) {
    if (out.length >= MAX_ERRORS) break
    if (el.closest(EXCLUDED_ANCESTORS) || !isVisible(el)) continue
    const raw = el.getAttribute('role') === 'alert' ? alertText(el) : invalidFieldText(el)
    const msg = shareableError(raw)
    if (msg && !out.includes(msg)) out.push(msg)
  }
  return out
}
